import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useProvinces,
  useAllDistricts, // Use useAllDistricts
  useAllCities, // Use useAllCities
} from "@/hooks/useLocations";
import { Province, District, City } from "@/lib/types";
import { useEffect, useMemo, useState, useCallback } from "react";
import { Badge } from "@/components/ui/badge";
import { X } from "lucide-react";

interface LocationFilterProps {
  selectedProvince: string | null; // Changed to string (slug)
  onSelectProvince: (provinceSlug: string | null) => void; // Changed to slug

  selectedDistrict: string | null; // Changed to string (slug)
  onSelectDistrict: (districtSlug: string | null) => void; // Changed to slug

  selectedCity: string | null; // Changed to string (slug)
  onSelectCity: (citySlug: string | null) => void; // Changed to slug

  onClearAllLocations: () => void;
}

const LocationFilter = ({
  selectedProvince,
  onSelectProvince,
  selectedDistrict,
  onSelectDistrict,
  selectedCity,
  onSelectCity,
  onClearAllLocations,
}: LocationFilterProps) => {
  const [isProvinceOpen, setIsProvinceOpen] = useState(false);
  const [isDistrictOpen, setIsDistrictOpen] = useState(false);
  const [isCityOpen, setIsCityOpen] = useState(false);

  // Fetch all location data
  const { data: provincesData, isLoading: isLoadingProvinces } = useProvinces();
  const { data: allDistrictsData, isLoading: isLoadingAllDistricts } =
    useAllDistricts();
  const { data: allCitiesData, isLoading: isLoadingAllCities } = useAllCities();

  // Filter districts based on selected province
  const districtsData = useMemo(() => {
    if (!selectedProvince || !allDistrictsData) return [];
    const province = provincesData?.find((p) => p.slug === selectedProvince);
    if (!province) return [];
    return allDistrictsData.filter((d) => d.province_id === province.id);
  }, [selectedProvince, allDistrictsData, provincesData]);

  const isLoadingDistricts = isLoadingAllDistricts;

  // Filter cities based on selected district
  const citiesData = useMemo(() => {
    if (!selectedDistrict || !allCitiesData) return [];
    const district = allDistrictsData?.find((d) => d.slug === selectedDistrict);
    if (!district) return [];
    return allCitiesData.filter((c) => c.district_id === district.id);
  }, [selectedDistrict, allCitiesData, allDistrictsData]);

  const isLoadingCities = isLoadingAllCities;

  // Memoize unique valid provinces (with slugs)
  const uniqueValidProvinces = useMemo(() => {
    const seenSlugs = new Set<string>();
    return (provincesData || []).filter(
      (province: Province | null | undefined): province is Province => {
        const isValid = !!(
          province &&
          province.slug &&
          typeof province.slug === "string" &&
          province.slug.trim() !== ""
        );
        if (isValid && !seenSlugs.has(province.slug)) {
          seenSlugs.add(province.slug);
          return true;
        }
        return false;
      }
    );
  }, [provincesData]);

  // Memoize unique valid districts from fetched data (with slugs)
  const uniqueValidDistricts = useMemo(() => {
    const seenSlugs = new Set<string>();
    return (districtsData || []).filter(
      (district: District | null | undefined): district is District => {
        const isValid = !!(
          district &&
          district.slug &&
          typeof district.slug === "string" &&
          district.slug.trim() !== ""
        );
        if (isValid && !seenSlugs.has(district.slug)) {
          seenSlugs.add(district.slug);
          return true;
        }
        return false;
      }
    );
  }, [districtsData]);

  // Memoize unique valid cities from fetched data (with slugs)
  const uniqueValidCities = useMemo(() => {
    const seenSlugs = new Set<string>();
    return (citiesData || []).filter(
      (city: City | null | undefined): city is City => {
        const isValid = !!(
          city &&
          city.slug &&
          typeof city.slug === "string" &&
          city.slug.trim() !== ""
        );
        if (isValid && !seenSlugs.has(city.slug)) {
          seenSlugs.add(city.slug);
          return true;
        }
        return false;
      }
    );
  }, [citiesData]);

  const getDisplayName = useCallback(
    (slug: string, type: "province" | "district" | "city") => {
      if (type === "province") {
        const item = provincesData?.find((p) => p.slug === slug);
        return item?.name || `Province: ${slug}`;
      } else if (type === "district") {
        const item = allDistrictsData?.find((d) => d.slug === slug);
        return item?.name || `District: ${slug}`;
      } else if (type === "city") {
        const item = allCitiesData?.find((c) => c.slug === slug);
        return item?.display_name || item?.name || `City: ${slug}`;
      }
      return slug;
    },
    [provincesData, allDistrictsData, allCitiesData]
  );

  // Handlers for single select - now pass slugs
  const handleProvinceSelect = (value: string) => {
    if (value === "all") {
      onSelectProvince(null);
    } else {
      onSelectProvince(value);
    }
    setIsProvinceOpen(false);
  };

  const handleDistrictSelect = (value: string) => {
    if (value === "all") {
      onSelectDistrict(null);
    } else {
      onSelectDistrict(value);
    }
    setIsDistrictOpen(false);
  };

  const handleCitySelect = (value: string) => {
    if (value === "all") {
      onSelectCity(null);
    } else {
      onSelectCity(value);
    }
    setIsCityOpen(false);
  };

  const hasAnyLocationSelected =
    selectedProvince !== null ||
    selectedDistrict !== null ||
    selectedCity !== null;

  return (
    <div className="p-4 border-b border-gray-200">
      <div className="flex justify-between items-center mb-3">
        <h3 className="font-medium text-sm text-gray-500">LOCATION</h3>
        {hasAnyLocationSelected && (
          <button
            onClick={onClearAllLocations}
            className="text-xs text-propertylk-blue hover:underline"
          >
            Clear All
          </button>
        )}
      </div>

      {/* Display selected locations as badges */}
      {hasAnyLocationSelected && (
        <div className="flex flex-wrap gap-1 mb-2">
          {/* Province badge */}
          {selectedProvince !== null && (
            <Badge
              key={`province-${selectedProvince}`}
              variant="secondary"
              className="pl-2 pr-1 py-0.5 text-sm bg-blue-100 text-blue-800 border border-blue-200"
            >
              {getDisplayName(selectedProvince, "province")}
              <button
                onClick={() => onSelectProvince(null)}
                className="ml-1 p-0.5 rounded-full hover:bg-blue-300"
                aria-label={`Remove ${getDisplayName(
                  selectedProvince,
                  "province"
                )} filter`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
          {/* District badge */}
          {selectedDistrict !== null && (
            <Badge
              key={`district-${selectedDistrict}`}
              variant="secondary"
              className="pl-2 pr-1 py-0.5 text-sm bg-blue-100 text-blue-800 border border-blue-200"
            >
              {getDisplayName(selectedDistrict, "district")}
              <button
                onClick={() => onSelectDistrict(null)}
                className="ml-1 p-0.5 rounded-full hover:bg-blue-300"
                aria-label={`Remove ${getDisplayName(
                  selectedDistrict,
                  "district"
                )} filter`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
          {/* City badge */}
          {selectedCity !== null && (
            <Badge
              key={`city-${selectedCity}`}
              variant="secondary"
              className="pl-2 pr-1 py-0.5 text-sm bg-blue-100 text-blue-800 border border-blue-200"
            >
              {getDisplayName(selectedCity, "city")}
              <button
                onClick={() => onSelectCity(null)}
                className="ml-1 p-0.5 rounded-full hover:bg-blue-300"
                aria-label={`Remove ${getDisplayName(
                  selectedCity,
                  "city"
                )} filter`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4">
        <Select
          onValueChange={handleProvinceSelect}
          value={selectedProvince !== null ? selectedProvince : "all"}
          open={isProvinceOpen}
          onOpenChange={setIsProvinceOpen}
        >
          <SelectTrigger
            className="w-full"
            onClick={() => setIsProvinceOpen(true)}
          >
            <SelectValue
              placeholder={
                selectedProvince !== null
                  ? getDisplayName(selectedProvince, "province")
                  : "All Provinces"
              }
            />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Provinces</SelectItem>
            {isLoadingProvinces ? (
              <SelectItem value="loading" disabled>
                Loading provinces...
              </SelectItem>
            ) : (
              uniqueValidProvinces.map((province) => (
                <SelectItem key={province.slug} value={province.slug || ""}>
                  {province.name} {selectedProvince === province.slug && "✓"}
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>

        {/* District Select */}
        <Select
          key={selectedProvince || "no-province"}
          onValueChange={handleDistrictSelect}
          value={selectedDistrict !== null ? selectedDistrict : "all"}
          open={isDistrictOpen}
          onOpenChange={setIsDistrictOpen}
          disabled={selectedProvince === null || isLoadingDistricts}
        >
          <SelectTrigger
            className="w-full"
            onClick={() => setIsDistrictOpen(true)}
          >
            <SelectValue
              placeholder={
                isLoadingDistricts
                  ? "Loading Districts..."
                  : selectedDistrict !== null
                  ? getDisplayName(selectedDistrict, "district")
                  : selectedProvince !== null
                  ? "Select District"
                  : "Select Province First"
              }
            />
          </SelectTrigger>
          <SelectContent>
            {isLoadingDistricts ? (
              <SelectItem value="loading" disabled>
                Loading districts...
              </SelectItem>
            ) : (
              <>
                <SelectItem value="all">All Districts</SelectItem>
                {uniqueValidDistricts.map((district) => (
                  <SelectItem key={district.slug} value={district.slug || ""}>
                    {district.name} {selectedDistrict === district.slug && "✓"}
                  </SelectItem>
                ))}
              </>
            )}
          </SelectContent>
        </Select>

        {/* City Select */}
        <Select
          key={selectedDistrict || "no-district"}
          onValueChange={handleCitySelect}
          value={selectedCity !== null ? selectedCity : "all"}
          open={isCityOpen}
          onOpenChange={setIsCityOpen}
          disabled={selectedDistrict === null || isLoadingCities}
        >
          <SelectTrigger className="w-full" onClick={() => setIsCityOpen(true)}>
            <SelectValue
              placeholder={
                isLoadingCities
                  ? "Loading Cities..."
                  : selectedCity !== null
                  ? getDisplayName(selectedCity, "city")
                  : selectedDistrict !== null
                  ? "Select City"
                  : "Select District First"
              }
            />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Cities</SelectItem>
            {isLoadingCities ? (
              <SelectItem value="loading" disabled>
                Loading cities...
              </SelectItem>
            ) : (
              uniqueValidCities.map((city) => {
                if (!city || !city.slug || city.name === "") {
                  console.error(
                    "[LocationFilter] Skipping render for invalid item inside map:",
                    city
                  );
                  return null;
                }
                return (
                  <SelectItem key={city.slug} value={city.slug || ""}>
                    {city.display_name || city.name || "Unknown City"}{" "}
                    {selectedCity === city.slug && "✓"}
                  </SelectItem>
                );
              })
            )}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

export default LocationFilter;
